<template>
	<figure
		v-if="isTable"
		class="cog-table cog-section"
		:class="[
			'cog-col',
			colNumClass,
			{
				'is-error': showValidation,
				'cog-table-bands': !chameleon && $source.readonly,
				'is-read-only': $source.readonly,
				['cog-field--' + fieldIndex]: fieldIndex,
				'cog-force-shown': forceShown,
				'show-remove-button': showRemoveButton,
				'hide-remove-button': !showRemoveButton
			}
		]"
		:tabindex="showValidation || showLengthError ? 0 : -1"
		@click="triggerErrors"
		@keyup="triggerErrors"
	>
		<figcaption v-if="showLabel || showHelptext">
			<h2 :is="'h' + Math.min(depth + 1, 4)" v-if="showLabel" class="cog-heading">
				{{ $source.label }}
				<span v-if="showValidation || showLengthError" class="cog-offscreen">, {{ errorMessage }}</span>
			</h2>
			<!-- eslint-disable-next-line vue/no-v-html -->
			<div v-if="showHelptext" class="cog-helptext cog-html" v-html="$source.helptext"></div>
		</figcaption>
		<table :class="{'is-compact': isCompact}">
			<colgroup>
				<col v-if="!$source.readonly" class="cog-table__delete-column" />
				<col v-for="column in $columns" :key="column.name" :style="{ width: column.width * 100 + '%' }" :class="{'cog-table__hidden-col': !column.visible}" />
				<col v-if="paddingCols" :class="'cog-col cog-col--' + paddingCols" />
			</colgroup>
			<thead>
				<tr>
					<th v-if="!$source.readonly">
						<!-- Corresponds to X buttons -->
					</th>
					<th v-for="column in $columns" :key="column.name" :class="{ 'is-required': column.property.required === true, 'cog-right': column.align === 'right', 'cog-table__hidden-col': !column.visible }">
						<!-- The asterisk and the label need to be on the same line in the markup. -->
						<div class="cog-label" :class="{ 'cog-offscreen': (column.hideLabel || !getPropertyLabel(column.property).trim().length) && !forceShowHeaders }">
							{{ getPropertyLabel(column.property) | breakWords }}<span v-if="showAsterisk(column)" aria-hidden="true" class="cog-asterisk">*</span>
							<span v-if="showAsterisk(column) && !showValidation" class="cog-offscreen">({{ $resource("required-asterisk") }})</span>
							<span v-if="column.property.required === true && showValidation" class="cog-offscreen">({{ $resource("required").replace("{property}", getPropertyLabel(column.property)) }})</span>
						</div>
					</th>
				</tr>
			</thead>
			<tbody is="transition-group" name="el-zoom-in-top" @after-leave="isAtMax = false">
				<vm-source v-for="($item, index) in $source.items" ref="tableRow" :key="$item.value.meta.id" :source="$item">
					<tr v-show="!$item.isHidden" :class="$item.class">
						<td v-if="!$source.readonly">
							<transition
								appear
								name="cog-fade"
							>
								<c-button v-if="showRemoveButton" class="cog-table__remove" no-style :title="getRemoveButtonText(itemLabel, $item.value.ItemNumber)" @click="e => removeItem(e, index)"><i-ex /></c-button>
							</transition>
						</td>
						<slot :item="$item.value" :adapter="$item" :isTable="true" :cols="cols" :generate-custom-class="generateCustomClass"></slot>
					</tr>
				</vm-source>
			</tbody>
			<tfoot v-if="anyColumnsHaveSummary" class="cog-table__summary">
				<tr>
					<td v-if="!$source.readonly"></td>
					<!-- Summary goes here -->
					<td v-for="column in $columns" :key="column.name" :class="{ 'cog-right': column.summaryAlign === 'right' }">
						<div v-if="column.visible && column.summary">
							<strong v-if="column.summaryLabel">{{ column.summaryLabel }}: </strong>
							<span>{{ getColumnSummary(column) }}</span>
						</div>
					</td>
				</tr>
			</tfoot>
		</table>
		<div class="cog-table__button-container">
			<visible-transition>
				<c-button
					v-if="showAddButton"
					class="cog-button--secondary cog-table__add"
					:class="{'cog-button--no-text': getAddButtonText(itemLabel) === ''}"
					:title="getAddButtonText(itemLabel) === '' ? getGeneratedAddButtonText(itemLabel) : null"
					@click="addItem"
				>
					{{ getAddButtonText(itemLabel) }}
					<template #icon><i-plus /></template>
				</c-button>
			</visible-transition>
		</div>
		<transition	name="cog-error-message">
			<div v-if="showValidation || showLengthError" class="cog-error-message cog-error-message--container-footer pt-1" :aria-live="showLabel ? null : 'polite'">{{ errorMessage }}</div>
		</transition>
	</figure>
	<c-repeating-section v-else ref="repeatingSection" :hide-label="hideLabel" :source="$source" :item-label="itemLabel" :add-button-text="addButtonText" :hide-add-remove-buttons="hideAddRemoveButtons" :min="min" :max="max" :cols="cols" :override-depth="depth" class="cog-table--repeating-section">
		<template #readonly-item-header="{item}">
			<slot name="readonly-item-header" :item="item" />
		</template>

		<template #default="{ item: item, adapter: $item }">
			<c-row>
				<slot :item="item" :adapter="$item" :isTable="false" :cols="cols" :generate-custom-class="generateCustomClass"></slot>
			</c-row>
		</template>
		<template v-if="anyColumnsHaveSummary" #footer>
			<div class="cog-row">
				<div class="cog-table__summary--responsive cog-col cog-col--24">
					<!-- Would be nice to have this localized. <h3 :is="'h' + Math.min(depth + 2, 4)" class="cog-offscreen">Summary</h3> -->
					<div v-for="(column, i) in $columns.filter(c => c.visible && c.summary)" :key="column.name" class="cog-table__summary-item--responsive">
						<h4 :is="'h' + Math.min(depth + 3, 4)" :id="`cog-${$source.id}-summary-${i}`" class="cog-heading">{{ getPropertyLabel(column.property) }}</h4>
						<div :aria-labelledby="`cog-${$source.id}-summary-${i}`">
							<span v-if="column.summaryLabel">{{ column.summaryLabel }}: </span>
							<span>{{ getColumnSummary(column) || '—' }}</span>
						</div>
					</div>
				</div>
			</div>
		</template>
	</c-repeating-section>
</template>

<script>
	import '../style/_table.scss';
	import '../style/_motion.scss';

	import ValidatedSourcePath from 'src/mixins/validated-source-path';
	import ResponsiveTable from 'src/mixins/responsive-table';
	import Col from './Col.vue';
	import setupKeyboard, { closeDownKeyboard } from './table-fields';
	import CRepeatingSection from './RepeatingSection.vue';
	import CButton from './Button.vue';
	import recordFocus from 'src/mixins/record-focus';
	import headingDepth from 'src/mixins/heading-depth';
	import showLabel from 'src/mixins/show-label';
	import IEx from 'src/assets/ex-outline.svg';
	import IPlus from 'src/assets/plus.svg';
	import VisibleTransition from './VisibleTransition.vue';

	export default {
		name: 'CTable',
		components: {
			CButton,
			CRepeatingSection,
			IEx,
			IPlus,
			VisibleTransition
		},
		mixins: [ValidatedSourcePath, ResponsiveTable, recordFocus, Col, headingDepth, showLabel],
		inject: {
			$resource: {},
			quantityService: {},
			flags: {}
		},
		props: {
			hideLabel: Boolean,
			itemLabel: {
				type: String,
				required: true
			},
			addButtonText: {
				type: String,
				default: null
			},
			columns: {
				/*
					align: "right" | "left"
					cols: Number
					name: String
					summary: Boolean
					visibility: "Always" | "Never" | "Internal" | "<visible property>"
				*/
				type: Array,
				required: true
			},
			chameleon: { type: Boolean, default: false },
			forceShowHeaders: { type: Boolean, default: false },
			fieldIndex: { type: String, default: null },
			forceShown: { type: Boolean, default: false },
			hideAddRemoveButtons: { type: Boolean, default: false },
			min: { type: Number, default: undefined },
			max: { type: Number, default: undefined }
		},
		data() {
			return {
				isCompact: false,
				hasInteracted: false
			};
		},
		computed: {
			columnVisibility() {
				return this.columns.reduce((map, c) => {
					map[c.name] = this.isColumnVisible(c);
					return map;
				}, {});
			},
			$columns() {
				const columns = this.columns.filter(c => c.visibility !== 'Never');
				return columns.map(c => {
					const property = this.$source.property.propertyType.meta.getProperty(c.name);
					const adjustment = this.columnAdjustments[c.name];
					const width = this.columnWidths[c.name];
					const visible = this.columnVisibility[c.name];
					const summaryProperty = this.$source.property.containingType.getProperty(`${this.$source.property.name}_${c.name}_Summary`);

					return {
						name: c.name,
						property: property,
						cols: c.cols,
						adjustment: adjustment,
						width: width,
						summary: c.summary || false,
						summaryLabel: c.summaryLabel || '',
						summaryAlign: summaryProperty && summaryProperty.propertyType === Number ? 'right' : 'left',
						visibility: c.visibility,
						visible: visible,
						align: c.align,
						hideLabel: c.hideLabel,
						readonly: c.readonly
					};
				});
			},
			anyColumnsHaveSummary() {
				return this.$columns.some(c => c.summary === true);
			},
			showLengthError() {
				return this.hasInteracted && !this.form.readonly && this.$source.conditions?.some(c => c.type.code.includes('ListLength'));
			},
			errorMessage() {
				return this.$source.firstError ? this.$source.firstError.message : null;
			},
			firstVisibleColumn() {
				let firstVisibleColumn = null;
				this.$columns.some((column, index) => {
					if (column.visible && !firstVisibleColumn) {
						firstVisibleColumn = column;
						return true;
					}
				});
				return firstVisibleColumn ? firstVisibleColumn.name : null;
			},
			lastVisibleColumn() {
				let lastVisibleColumn = null;
				this.$columns.forEach(column => {
					if (column.visible)
						lastVisibleColumn = column;
				});
				return lastVisibleColumn ? lastVisibleColumn.name : null;
			},
			showHelptext() {
				return this.$source.helptext && !this.$source.readonly;
			},
			showAddButton() {
				if (!this.$source.readonly && !this.max && this.max !== 0)
					return true;

				const isAtMax = this.max != null ? this.tableLength >= this.max : false;
				return !this.$source.readonly && !(this.hideAddRemoveButtons && isAtMax);
			},
			showRemoveButton() {
				if (!this.$source.readonly && !this.min)
					return true;
				return !this.$source.readonly && !(this.hideAddRemoveButtons && this.tableLength <= this.min);
			},
			tableLength() {
				return this.$source.value.length;
			}
		},
		watch: {
			isTable(isTable, wasTable) {
				const validationData = this.getFieldsWithErrors();
				this.$nextTick(() => {
					if (validationData.size)
						this.validateFields(validationData);
					if (wasTable && (this.showValidation || this.showLengthError))
						this.$refs.repeatingSection.forceValidation();
					if (isTable)
						setupKeyboard(this.$el);
					else
						closeDownKeyboard(this.$el);
				});
			}
		},
		mounted() {
			setupKeyboard(this.$el);
			window.addEventListener('resize', this.checkIfCompact);
			this.checkIfCompact();
		},
		beforeDestroy() {
			window.removeEventListener('resize', this.checkIfCompact);
		},
		methods: {
			triggerErrors() {
				this.hasInteracted = true;
			},
			checkIfCompact() {
				requestAnimationFrame(()=>{
					const tableWidth = this.$el.clientWidth;
					const smallestCol = this.$columns.reduce((acc, cur) => Math.min(acc, cur.width), 100);
					const narrowCellThreshold = 90;
					this.isCompact = tableWidth * smallestCol < narrowCellThreshold;
				});
			},
			getRemoveButtonText(itemLabel, itemNumber) {
				return (this.$resource('field-repeatingsection-remove-link-text').includes('{item}') ?
					this.$resource('field-repeatingsection-remove-link-text') :
					this.$resource('field-repeatingsection-remove-link-text') + ' {item}').replace('{item}', itemLabel + ' ' + itemNumber);
			},
			getAddButtonText(itemLabel) {
				return this.addButtonText || this.addButtonText === '' ? this.addButtonText : this.getGeneratedAddButtonText(itemLabel);
			},
			getGeneratedAddButtonText(itemLabel) {
				return (this.$resource('field-repeatingsection-add-link-text').includes('{item}') ?
					this.$resource('field-repeatingsection-add-link-text') :
					this.$resource('field-repeatingsection-add-link-text') + ' {item}').replace('{item}', itemLabel);
			},
			getFieldContainers() {
				if (this.$refs.repeatingSection && this.$refs.repeatingSection.$refs.repeatingSection)
					return this.$refs.repeatingSection.$refs.repeatingSection.map(section => section.$children.find(c => c.$options.name === 'CRow')) || [];
				else
					return this.$refs.tableRow || [];
			},
			validateFields(validationData) {
				this.getFieldContainers().forEach((container, index) => {
					const invalidSources = validationData.get(index);
					if (Array.isArray(invalidSources)) {
						for (const field of container.$children.filter(c => c.$options.name === 'CField'))
							if (invalidSources.includes(field.source))
								field.forceValidation();
					}
				});
			},
			getFieldsWithErrors() {
				const validationData = new Map();
				this.getFieldContainers().forEach((container, index) => {
					const invalidatedSources = [];
					for (const field of container.$children.filter(c => c.$options.name === 'CField'))
						if (field.showValidation)
							invalidatedSources.push(field.source);

					if (invalidatedSources.length)
						validationData.set(index, invalidatedSources);
				});
				return validationData;
			},
			addItem(event) {
				this.lostFocus = true; // validation should now run when adding an item (#17831)
				const list = this.$source.value;
				const JSType = this.$source.property.propertyType;
				list.push(new JSType());
			},
			removeItem(event, index) {
				this.recordFocus();
				const list = this.$source.value;
				list.splice(index, 1);
			},
			isColumnVisible(column) {
				if (column.visibility === 'Always') {
					return true;
				}
				else if (column.visibility === 'Never') {
					return false;
				}
				else if (column.visibility === 'Internal') {
					return this.flags.internal;
				}
				else {
					// The summary property name begins with the table property name
					const tablePropertyName = this.$source.property.name;

					// Get the conditional visibility property from the parent source
					const parentSource = this.$source.parent;
					const containingType = this.$source.property.containingType;
					const conditionalVisibilityProperty = containingType.getProperty(`${tablePropertyName}_${column.name}_IsVisible`);
					if (!conditionalVisibilityProperty)
						return true;

					// Get the value of the conditional visibility property
					return conditionalVisibilityProperty.value(parentSource.value) || !this.flags.conditionalVisibility;
				}
			},
			getColumnSummary(column) {
				// The summary property name begins with the table property name
				const tablePropertyName = this.$source.property.name;

				// Get the summary property from the parent source
				const parentSource = this.$source.parent;
				const containingType = this.$source.property.containingType;
				const summaryProperty = containingType.getProperty(`${tablePropertyName}_${column.name}_Summary`);
				if (!summaryProperty) {
					return;
				}

				// Get the value of the summary property
				// NOTE: Use `toString` so that the property's format will be used
				return parentSource.value.toString('[' + summaryProperty.name + ']');
			},
			generateCustomClass(columnName) {
				const customClasses = [];
				if (columnName === this.firstVisibleColumn)
					customClasses.push('cog-table__first-col');
				if (columnName === this.lastVisibleColumn)
					customClasses.push('cog-table__last-col');
				return customClasses.join(' ');
			},
			getPropertyLabel(property) {
				return property.labelIsFormat ? this.$source.parent.value.toString(property.label) : !property.label ? '' : property.label;
			},
			showAsterisk(column) {
				return column.property.required && !this.form.isReadonly;
			}
		}
	};
</script>

<style lang="scss">
#{$specificity-base} {

	/*
		A. .cog-table
			1. Not Read-only
			2. Read-only
			3. General .cog-table styles
				a. Cell-level error messages
				b. Hidden Columns
			4. Non-Chameleon .cog-table styles
	*/
	$indentation-amount: calc(1.7em + 3px);
	$table-indent: $indentation-amount;
	$table-outdent: calc((#{$indentation-amount}) * -1);

	.cog-table {

		table.is-compact {
			--input__padding-h: 5px;
		}

		@include at-root(2) {

			.cog-table {
				padding-top: $gutter-quarter;
			}
		}

		&:not(.is-read-only) {

			.cog-table__button-container {
				padding-bottom: $gutter-two-thirds;
			}

			// Because textareas and read-only inputs can be arbitrary heights, other inputs
			// must grow to be height of the tallest cell in the row. This is done by flexbox,
			// but tds can't be set to display: flex so the first child of the td needs to be
			// set to 100% height of the cell, then it's children can be flex items.
			table {
				height: 1px;
				border-spacing: var(--cell__spacing-h, 0) var(--cell__spacing-v, 0);
			}

			tr,
			td,
			td div {
				height: 100%;
			}

			textarea {
				min-height: calc(100% + var(--input__border-width)) !important;
			}

			.is-read-only {
				@include input-accidental;
				@include disabled-input;
				display: inline-flex;
				flex-direction: column;
				justify-content: center;
			}

			.cog-input.is-read-only {
				padding: var(--input__padding-v) var(--input__padding-h);
			}

			tr > * {
				background: transparent;
			}

			// 💪 = contrary to the class name, want to zoom in from middle
			tr.el-zoom-in-top-leave-active {
				transform-origin: center top; // 💪
				transition: .3s;

				// for Safari #19989
				will-change: transform;
			}
		}

		&.is-read-only {
			table-layout: auto;

			// Let all spacing come from td
			.cog-input {
				margin: 0;
				padding: 0;
			}

			tr > * {
				padding: var(--input__padding-v) var(--input__padding-h);
			}

			tfoot td {
				border-top: hsla(var(--background-hsl), 1), var(--border-width) solid;
			}
		}

		// General .cog-table styles
		/* stylelint-disable-next-line no-duplicate-selectors */
		& {

			table {
				table-layout: fixed;
				border-collapse: initial;
			}

			// Tables that don't have headers need a little space before the section header.
			tbody:first-child::before {
				display: block;
				height: $gutter-quarter;
				content: '';
			}

			td {
				position: relative;
				padding: 0;
			}

			td[aria-hidden][aria-hidden]::before {
				display: block;
				position: relative;
				left: calc(var(--input__border-width) * -.5);
				top: 0;
				width: calc(100% + var(--input__border-width));
				height: calc(100% + var(--input__border-width));
				background: var(--form__background-color);
				background-image: linear-gradient(hsla(var(--background-hsl), .25) 0%, hsla(var(--background-hsl), .25) 100%);
				cursor: not-allowed;
				content: '';
			}

			col {
				transition: $speed-half;
			}

			th {
				// Asterisks are set to position:absolute so they'll never wrap to the next line without the last word of the label.
				// This causes them to be seen on a conditionally hidden column set to overflow:hidden. Making the header position:relative
				// fixes this. Cf. 19212
				position: relative;
				overflow: hidden;
				padding-top: calc(var(--input__padding-v) + var(--input__border-width-left, var(--input__border-width, 1px)) / 2);
				padding-right: calc(var(--input__padding-h) + var(--input__border-width-left, var(--input__border-width, 1px)) / 2);
				padding-left: calc(var(--input__padding-h) + var(--input__border-width-left, var(--input__border-width, 1px)) / 2);
				text-align: left;

				@include at-root(1) {

					.cog-table th {
						padding-bottom: 1px;
					}
				}
			}

			th > div {
				overflow: hidden;
				-webkit-hyphens: auto;
				-ms-hyphens: auto;
				hyphens: auto;
			}

			// Since form builders didn't design it this way, don't strictly
			// conform to the column settings of these fields requiring more space.
			&--repeating-section {

				.cog-date {
					min-width: 8.7em;
				}

				.cog-choice--dropdown {
					min-width: 7.5em;
				}

				.cog-yesno--toggle {
					min-width: 9em;
				}
			}

			&__delete-column {
				// The percentage unit is essential for Edge.
				width: 0%;
			}

			td .cog-table__remove {
				margin-left: $table-outdent;
				font-size: var(--font-size);
			}

			&__add {
				max-width: 100%;
				margin-top: $gutter-two-thirds;

				// zero needs unit on below line
				margin-left: calc((var(--input__border-width-left, var(--input__border-width)) * -.5px) + (var(--cell__spacing-h, 0px) * 2));
				white-space: nowrap;

				.cog-button__text {
					overflow: hidden;
					text-overflow: ellipsis;
				}
			}

			&__bottom-container {
				margin-bottom: $gutter-half;
			}

			&__summary td {
				padding-top: var(--input__padding-v);
				padding-right: var(--input__padding-h);
				padding-left: var(--input__padding-h);
			}

			&__summary-item--responsive {
				margin-bottom: $gutter-quarter;

				&:first-child {
					border-top: calc(var(--border-width) + 1px) solid hsla(var(--background-hsl), 1);
					padding-top: $gutter-half;
				}
			}

			@mixin table-cell {
				position: relative;
				z-index: $normal-border-cell--z;

				// Get input borders to overlap
				width: calc(100% + var(--input__border-width));
				min-height: calc(100% + var(--input__border-width));
				margin: calc(var(--input__border-width) * -.5);
				line-height: var(--input__line-height);
			}

			// Apple's ways are not our ways
			@include at-root {

				[data-browser='safari'] .cog-table {

					[type='text'],
					[type='email'],
					[type='url'],
					textarea {
						top: .5px;
					}
				}
			}

			.cog-input.is-read-only,
			.el-input__inner {
				height: auto !important;
			}

			.el-input__inner.el-input__inner,
			.el-textarea__inner,
			.cog-input.is-read-only,
			tfoot td > div {
				@include table-cell;
			}

			@include chameleon {

				.cog-table select {
					@include table-cell;
				}
			}

			@include styled {

				.cog-table {

					.el-input__inner.el-input__inner,
					.el-textarea__inner,
					.cog-input.is-read-only {
						border-radius: 0;

						&:focus {
							z-index: $special-border-cell--z;
						}
					}
				}
			}

			.cog-label {
				padding-bottom: 4px;
			}

			.cog-input:not(.is-read-only) {
				// Override normal input spacing
				margin: 0;
				padding-top: 0;
			}

			// Hide the icon for dropdowns within a table until focused (#5995)
			td:not(.is-focus) .cog-picker .el-input__suffix {
				// setting this to display:none or visibility:hidden messes up picker icon click.
				opacity: 0;
			}

			.cog-right,
			.cog-number,
			.cog-currency,
			.cog-calculation--decimal,
			.cog-calculation--percent,
			.cog-calculation--currency,
			.cog-number input,
			.cog-currency input {
				text-align: right;
			}

			.el-select {
				position: static;
			}

			// Cell-level error messages
			td .cog-error-message.cog-error-message {
				// 💥 = Override general error message styles
				// 🦕 = Override height: 100% on trs, tds, and divs in tables
				position: absolute;
				left: calc(var(--input__border-width) * -.5);
				top: 100%;
				z-index: $error-cell--z;
				width: calc(100% + var(--input__border-width)); // 💥
				height: auto; // 🦕
				margin: 0; // 💥
				overflow: hidden;
				transform: scaleY(0);
				transform-origin: 0 0;
				transition: all $speed-quarter;
			}

			.is-error {

				.el-input__inner {
					position: relative;
					z-index: $error-cell--z;
					border-color: var(--negative) !important;
				}

				&:not(.is-focus) .el-input__inner {
					padding-right: $icon-input-padding;
				}

				.cog-error-message__icon {
					position: absolute;
					right: 3px;
					top: 0;
					z-index: $error-cell--z;
					height: 100%;
					pointer-events: none;
					touch-action: none;
				}

				&.is-focus .cog-error-message,
				&:hover .cog-error-message {
					z-index: $error-cell-message--z;
					transform: scaleY(1);
				}

				&.cog-hidden-validation .cog-error-message__icon-container {
					position: absolute;
					right: -30px;
					top: 0;
					z-index: $error-cell--z;
					height: 100%;
					opacity: 1;
				}

				&.is-focus .cog-error-message__icon-container {
					display: none; // For IE opacity doesn't work https://stackoverflow.com/questions/23504400/css-opacity-not-working-in-ie11
				}
			}
		}
	}

	.show-remove-button > table {
		padding-left: $table-indent;
		transition: .3s;
		will-change: padding;
	}

	.hide-remove-button > table {
		transition: .3s;
		will-change: padding;
	}

	// Hidden columns
	col.cog-table__hidden-col {
		/* stylelint-disable-next-line number-max-precision */
		width: .0000001% !important;

		@media print {
			overflow: hidden;
		}
	}

	@media print {

		.cog-table__hidden-col {
			visibility: collapse;
		}
	}

	th:not(.cog-table__hidden-col) > div {
		max-height: 1000px;
		transition: all calc(var(--speed) * 4) $speed-quarter;
	}

	th.cog-table__hidden-col > div {
		max-height: 0;
	}

	td.cog-table__hidden-col > * {
		opacity: 0;
		transition: opacity $speed-half $speed-quarter;
	}

	@include styled {

		.cog-table {

			td:not(.is-focus):not(.is-error) .cog-picker .el-input__inner {
				padding-right: var(--input__padding-h);
			}

			.el-input__inner:not(:focus),
			.el-textarea__inner:not(:focus),
			.cog-input.is-read-only {
				border-color: var(--cell__border-color, var(--input__border-color));
			}

			// Because of conditionally visible cells, these will need a first and last class applied with JS rather than relying on nth-child.
			tr:first-child .cog-table__first-col::before,
			tr:first-child .cog-table__first-col .el-input__inner,
			tr:first-child .cog-table__first-col .el-textarea__inner,
			tr:first-child .cog-table__first-col .cog-input.is-read-only {
				border-top-left-radius: var(--input__border-radius);
			}

			tr:first-child .cog-table__last-col::before,
			tr:first-child .cog-table__last-col .el-input__inner,
			tr:first-child .cog-table__last-col .el-textarea__inner,
			tr:first-child .cog-table__last-col .cog-input.is-read-only {
				border-top-right-radius: var(--input__border-radius);
			}

			tr:last-child .cog-table__first-col::before,
			tr:last-child .cog-table__first-col .el-input__inner,
			tr:last-child .cog-table__first-col .el-textarea__inner,
			tr:last-child .cog-table__first-col .cog-input.is-read-only {
				border-bottom-left-radius: var(--input__border-radius);
			}

			tr:last-child .cog-table__last-col::before,
			tr:last-child .cog-table__last-col .el-input__inner,
			tr:last-child .cog-table__last-col .el-textarea__inner,
			tr:last-child .cog-table__last-col .cog-input.is-read-only {
				border-bottom-right-radius: var(--input__border-radius);
			}
		}
	}
}
</style>